import React, { useState, useEffect, useRef, useMemo } from 'react';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import deliveryOrderService from '../../services/deliveryOrderService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFileInvoiceDollar, faSpinner } from '@fortawesome/free-solid-svg-icons';
import SupervisorHistoryButton from '../common/SupervisorHistoryButton';
import useAuth from '../../hooks/useAuth';
import { USER_ROLES } from '../../utils/constants';
import { useTranslation } from 'react-i18next';

const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const GenerateChallanModal = ({ isOpen, onClose, orderId, onConfirm, loading = false }) => {
  const { t } = useTranslation();
  const [date] = useState(todayIso());
  const [orderData, setOrderData] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  // Form State
  const [expenses, setExpenses] = useState('');
  const [expenseRemark, setExpenseRemark] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountRemark, setDiscountRemark] = useState('');
  const [priceOverrides, setPriceOverrides] = useState({}); // { item_id: 'price_string' }
  const [weightOverrides, setWeightOverrides] = useState({}); // { item_id: 'weight_string' }
  const [emptyTruckWeightOverride, setEmptyTruckWeightOverride] = useState('');
  const [roundingOff, setRoundingOff] = useState('');
  const [loadingCostPerKg, setLoadingCostPerKg] = useState('');
  const [orderRemark, setOrderRemark] = useState('');

  // Direct-flow fields
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleNumberError, setVehicleNumberError] = useState(false);
  const [driverContact, setDriverContact] = useState('');
  const [vehicleRemark, setVehicleRemark] = useState('');
  const [finalTruckWeight, setFinalTruckWeight] = useState('');
  const [itemsUpdate, setItemsUpdate] = useState({}); // { item_id: { actual_quantity, actual_weight, actual_pieces, remark } }

  const auth = useAuth();
  const isAccountExecutive = auth.hasRole(USER_ROLES.ACCOUNT_USER) || auth.hasRole(USER_ROLES.OPERATIONS_MANAGER);

  const challanStrings = t('deliveryOrdersModule.forms.challan', { returnObjects: true });
  const labels = challanStrings.labels || {};
  const placeholders = challanStrings.placeholders || {};
  const buttons = challanStrings.buttons || {};

  const lastFetchedOrderId = useRef(null);

  useEffect(() => {
    if (isOpen && orderId && lastFetchedOrderId.current !== orderId) {
      lastFetchedOrderId.current = orderId;
      fetchOrderDetails();
      // Reset form
      setExpenses('');
      setExpenseRemark('');
      setDiscount('');
      setDiscountRemark('');
      setPriceOverrides({});
      setWeightOverrides({});
      setEmptyTruckWeightOverride('');
      setRoundingOff('');
      setLoadingCostPerKg('');
      setOrderRemark('');
      setVehicleNumber('');
      setVehicleNumberError(false);
      setDriverContact('');
      setVehicleRemark('');
      setFinalTruckWeight('');
      setItemsUpdate({});
    }
    if (!isOpen) {
      lastFetchedOrderId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]);

  const fetchOrderDetails = async () => {
    setFetching(true);
    setError(null);
    try {
      const res = await deliveryOrderService.getDeliverySubOrderById(orderId, { force: true });
      if (res.success) {
        setOrderData(res.data);
        setLoadingCostPerKg(parseFloat(res.data.loading_cost_per_kg || 0).toFixed(2));
        setOrderRemark(res.data.order_remark || '');
        setEmptyTruckWeightOverride(res.data.empty_truck_weight ? String(res.data.empty_truck_weight) : '');
      } else {
        setError(res.error || challanStrings.messages.fetchError);
      }
    } catch (err) {
      setError(err.message || challanStrings.messages.fetchError);
    } finally {
      setFetching(false);
    }
  };

  const { gstPercentage, effectiveSubtotal, totalWithoutGst, totalGst, totalWithGst, freightAmount, freightType, currentCalculatedWeight, currentEmptyTruckWeight } = useMemo(() => {
    if (!orderData) return { effectiveSubtotal: 0, freightAmount: 0, freightType: '', totalWithGst: 0 };

    const gstPerc = parseFloat(orderData.items?.[0]?.gst_percentage) || 18.0;

    const isDirectFlow = !!orderData.is_direct_flow;

    const effectiveSubtotal = (orderData.items || []).reduce((sum, item) => {
      const effectivePrice = parseFloat(priceOverrides[item.id] ?? item.price ?? 0);
      const baseQty = isDirectFlow
        ? parseFloat(itemsUpdate[item.id]?.actual_quantity ?? item.quantity ?? 0)
        : parseFloat(item.actual_quantity || 0);
      const qty = Math.round(baseQty);
      return sum + effectivePrice * qty;
    }, 0);

    const effectiveItemGst = effectiveSubtotal * (gstPerc / 100);
    const freight = parseFloat(orderData.freight) || 0;
    const loadingCostPerKgValue = parseFloat(loadingCostPerKg) || 0;
    const calculatedTruckWeight = parseFloat(orderData.calculated_truck_weight) || 0;
    const loadingCost = calculatedTruckWeight > 0
      ? loadingCostPerKgValue * calculatedTruckWeight
      : parseFloat(orderData.total_loading_cost) || 0;
    const freightGst = parseFloat(orderData.freight_gst_amount) || 0;
    const loadingGst = loadingCost * (gstPerc / 100);

    const fType = orderData.freight_type || '';
    const isFreightChargeable = fType === 'freight_chargeable';
    const isFreightToPay = fType === 'free_delivery_freight_to_pay';
    const freightForTotal = isFreightChargeable ? freight : (isFreightToPay ? -freight : 0);
    const freightGstForTotal = isFreightChargeable ? freightGst : 0;

    const newExpensesVal = parseFloat(expenses) || 0;
    const newExpenseGstVal = newExpensesVal * (gstPerc / 100);
    const newDiscountVal = parseFloat(discount) || 0;
    const newDiscountGstVal = newDiscountVal * (gstPerc / 100);

    // Dynamic calculations for weights
    const currentEmptyTruckWeight = emptyTruckWeightOverride !== ''
      ? parseFloat(emptyTruckWeightOverride)
      : parseFloat(orderData.empty_truck_weight) || 0;
    const currentCalculatedWeight = (orderData.items || []).reduce((sum, item) => {
      const weight = weightOverrides[item.id] !== undefined
        ? parseFloat(weightOverrides[item.id])
        : parseFloat(item.actual_weight) || 0;
      return sum + weight;
    }, 0);

    const withoutGst = effectiveSubtotal + loadingCost + freightForTotal + newExpensesVal - newDiscountVal;
    
    // If GST is included, don't add any additional GST amount to the total
    const isGstIncluded = !!orderData.gst_included;
    const totalGstVal = isGstIncluded 
      ? 0 
      : (effectiveItemGst + freightGstForTotal + loadingGst + newExpenseGstVal - newDiscountGstVal);

    return {
      gstPercentage: gstPerc,
      effectiveSubtotal,
      totalWithoutGst: withoutGst,
      totalGst: totalGstVal,
      totalWithGst: withoutGst + totalGstVal,
      freightType: fType,
      freightAmount: freight,
      currentEmptyTruckWeight,
      currentCalculatedWeight,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData, expenses, discount, priceOverrides, weightOverrides, emptyTruckWeightOverride, loadingCostPerKg, isAccountExecutive, itemsUpdate]);

  const autoRoundingOff = parseFloat((Math.round(totalWithGst) - totalWithGst).toFixed(2));
  const roundingOffValue = roundingOff !== '' ? parseFloat(roundingOff) || 0 : autoRoundingOff;
  const grandTotal = totalWithGst + roundingOffValue;

  const displayFields = useMemo(() => {
    if (!orderData) return [];
    const excludedKeys = [
      'id', 'order_id', 'created_at', 'updated_at', 'created_by',
      'items', 'hsn_codes', 'delivery_supervisor_id', 'challan_generated', 'total_loading_cost', 'loading_cost_gst_amount',
      'challan_date', 'challan_number', 'sub_order_number', 'customer_name', 'weight_entries_list', 'expense_gst_amount', 'discount_gst_amount','expenses','discount', 'labour_ids',
      'unit_type', 'status', 'subtotal', 'total_weight', 'loading_cost_per_kg', 'freight', 'freight_gst_amount', 'total_gst_amount', 'total_amount', 'total_items', 'total_quantity', 'total_delivered_quantity', 'remark',
      'supervisor_name', 'supervisor_history',
      'is_direct_flow', 'requires_supervisor', 'challan_generated_at', 'challan_generated_by', 'challan_printed', 'challan_printed_at',
      'vehicle_number', 'driver_contact', 'vehicle_remark', 'final_truck_weight',
      'warehouse_id', 'warehouse_name', 'order_number', 'sdo_created_by_name', 'do_created_by_name', 'challan_generated_by_name',
      'assigned_by_name', 'created_by', 'customer_address', 'location_name', 'location_address', 'expected_delivery_date',
      'gst_included', 'freight_rate', 'calculated_truck_weight', 'total_items'
    ];

    return Object.entries(orderData)
      .filter(([key, value]) => {
        if (value === null || value === undefined) return false;
        if (excludedKeys.includes(key)) return false;
        if (key.endsWith('_id')) return false;
        if (key.includes('remark')) return false;
        // Don't show empty_truck_weight in displayFields if we're showing it as an editable input
        if (isAccountExecutive && (key === 'empty_truck_weight' || key === 'final_truck_weight' || key === 'calculated_truck_weight')) return false;
        return true;
      })
      .map(([key, value]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const freightTypeLabels = {
          freight_chargeable: 'Freight Chargeable',
          free_delivery_freight_to_pay: 'Freight To Pay',
          ex_warehouse: 'Ex Warehouse',
          free_delivery_by_supplier: 'Free by Supplier',
          free_delivery_paid_by_company: 'Freight Paid by Company',
        };
        const freightPricingLabels = { fixed: 'Fixed', per_kg: 'Per KG' };
        const displayValue = [
          'subtotal', 'loading_cost_per_kg', 'total_loading_cost', 'loading_cost_gst_amount',
          'freight', 'freight_gst_amount', 'total_gst_amount', 'total_amount'
        ].includes(key)
          ? `₹ ${value}`
          : ['total_quantity', 'total_delivered_quantity', 'empty_truck_weight', 'final_truck_weight', 'calculated_truck_weight'].includes(key)
          ? ['total_quantity', 'total_delivered_quantity'].includes(key)
          ? `${Math.round(parseFloat(value || 0))} ${orderData.items?.[0]?.unit || (orderData.unit_type?.toLowerCase() === 'meter' ? 'm' : 'kg')}`
          : `${Math.round(parseFloat(value || 0))} kg`
          : key === 'freight_type'
          ? (freightTypeLabels[value] || String(value).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
          : key === 'freight_pricing_type'
          ? (freightPricingLabels[value] || String(value).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
          : ['unit_type', 'status'].includes(key)
          ? String(value).toUpperCase()
          : value;
        return { key, label, value: displayValue };
      });
  }, [orderData, isAccountExecutive]);

  const handleSubmit = () => {
    if (!orderId || !orderData?.items) return;

    const isDirectFlow = !!orderData.is_direct_flow;

    if (isDirectFlow && !vehicleNumber.trim()) {
      setVehicleNumberError(true);
      return;
    }

    const item_price_overrides = orderData.items.map(item => {
      const overridePrice = priceOverrides[item.id];
      const price = (overridePrice !== undefined && overridePrice !== '')
        ? parseFloat(overridePrice)
        : parseFloat(item.price) || 0;

      return {
        item_id: Number(item.id),
        price: price
      };
    });

    const payload = {
      challan_date: date,
      expenses: parseFloat(expenses) || 0,
      expense_remark: expenseRemark,
      discount: parseFloat(discount) || 0,
      discount_remark: discountRemark,
      item_price_overrides,
      // Only a value the operator TYPED is sent; otherwise the backend rounds the
      // total it computes (policy P3-3). Sending the preview's auto value pinned
      // a rounding computed from the form's own (sometimes different) arithmetic.
      rounding_off: roundingOff !== '' ? roundingOffValue : undefined,
      loading_cost_per_kg: parseFloat(loadingCostPerKg) || 0,
      order_remark: orderRemark,
    };

    if (isDirectFlow) {
      payload.vehicle_number = vehicleNumber;
      payload.driver_contact = driverContact;
      payload.vehicle_remark = vehicleRemark;
      payload.empty_truck_weight = emptyTruckWeightOverride !== '' ? parseFloat(emptyTruckWeightOverride) : undefined;
      payload.final_truck_weight = finalTruckWeight !== '' ? parseFloat(finalTruckWeight) : undefined;
      payload.items_update = orderData.items.map(item => {
        const upd = itemsUpdate[item.id] || {};
        return {
          id: Number(item.id),
          actual_quantity: upd.actual_quantity !== undefined ? parseFloat(upd.actual_quantity) : undefined,
          actual_weight: upd.actual_weight !== undefined ? parseFloat(upd.actual_weight) : undefined,
          actual_pieces: upd.actual_pieces !== undefined ? parseFloat(upd.actual_pieces) : undefined,
          remark: upd.remark,
        };
      });
    } else {
      payload.empty_truck_weight = emptyTruckWeightOverride !== '' ? parseFloat(emptyTruckWeightOverride) : orderData.empty_truck_weight;
      payload.item_weight_overrides = Object.entries(weightOverrides).map(([id, weight]) => ({
        item_id: Number(id),
        actual_weight: parseFloat(weight)
      }));
    }

    onConfirm?.(orderId, payload);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <div
        className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        <div
          className="flex items-center justify-between p-6 border-b shrink-0"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {challanStrings.title}
            </h2>
            <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {challanStrings.subtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:opacity-80"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto grow">
          {fetching ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              />
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {challanStrings.messages.fetching}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-500 font-medium">
              {error}
            </div>
          ) : orderData ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {labels.subOrder}
                  </label>
                  <div
                    className="w-full px-4 py-3 rounded-xl border-2"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.FORMS.INPUT.text,
                    }}
                  >
                    {orderData.sub_order_number}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {labels.customer}
                  </label>
                  <div
                    className="w-full px-4 py-3 rounded-xl border-2 truncate"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.FORMS.INPUT.text,
                    }}
                    title={orderData.customer_name}
                  >
                    {orderData.customer_name}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {challanStrings.selectDateLabel}
                  </label>
                  <div
                    className="w-full px-4 py-3 rounded-xl border-2"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border,
                      color: UI_COLORS.FORMS.INPUT.text,
                    }}
                  >
                    {date}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION, borderColor: UI_COLORS.BORDERS.LIGHT }}>
                {displayFields.map((field) => (
                  <div key={field.key}>
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      {field.label}
                    </div>
                    <div className="text-sm font-medium text-gray-900 truncate" title={String(field.value)}>
                      {String(field.value)}
                    </div>
                  </div>
                ))}
                
                {isAccountExecutive && (
                  <>
                    <div>
                      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                        Empty Truck Load
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          value={emptyTruckWeightOverride}
                          onChange={(e) => setEmptyTruckWeightOverride(e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className="w-full px-2 py-1 rounded border text-sm font-bold focus:outline-none focus:border-blue-400"
                          style={{
                            backgroundColor: '#fff',
                            borderColor: emptyTruckWeightOverride !== '' ? '#3b82f6' : UI_COLORS.BORDERS.LIGHT,
                          }}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 font-bold">kg</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                        {t('common.formFields.labels.calculatedWeight')}
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {Math.round(currentCalculatedWeight)} kg
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                        {t('common.formFields.labels.netWeight')}
                      </div>
                      <div className="text-sm font-bold text-blue-600">
                        {Math.round(currentCalculatedWeight - currentEmptyTruckWeight)} kg
                      </div>
                    </div>
                  </>
                )}

                <SupervisorHistoryButton
                  label={t('common.formFields.labels.supervisor')}
                  supervisorName={orderData.supervisor_name}
                  supervisorHistory={orderData.supervisor_history || []}
                />
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.orderRemark')}</p>
                <textarea
                  value={orderRemark}
                  onChange={(e) => setOrderRemark(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border text-xs font-medium resize-none focus:outline-none focus:border-yellow-400 transition-colors"
                  style={{ backgroundColor: '#fefce8', borderColor: '#fde68a', color: '#92400e' }}
                  placeholder={t('common.formFields.placeholders.addOrderRemark')}
                />
              </div>

              {/* Direct flow: vehicle + truck weight fields */}
              {orderData.is_direct_flow && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl border-2" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', borderColor: 'rgba(13, 148, 136, 0.3)' }}>
                  <div className="md:col-span-3">
                    <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#0d9488' }}>{t('common.formFields.labels.vehicleAndDispatchDetails')}</p>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: vehicleNumberError ? '#ef4444' : UI_COLORS.TEXT.MUTED }}>
                      {t('common.formFields.labels.vehicleNumber')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={vehicleNumber}
                      onChange={(e) => { setVehicleNumber(e.target.value.slice(0, 20)); if (e.target.value.trim()) setVehicleNumberError(false); }}
                      maxLength={20}
                      className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                      style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: vehicleNumberError ? '#ef4444' : UI_COLORS.FORMS.INPUT.border }}
                      placeholder={t('common.formFields.placeholders.vehicleNumberExample')}
                    />
                    {vehicleNumberError && <p className="mt-1 text-xs text-red-500 font-medium">{t('common.formFields.errors.vehicleNumberRequired')}</p>}
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.driverContact')}</label>
                    <input
                      type="text"
                      value={driverContact}
                      onChange={(e) => setDriverContact(e.target.value.slice(0, 20))}
                      maxLength={20}
                      className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                      style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                      placeholder={t('common.formFields.placeholders.driverContactExample')}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.vehicleRemark')}</label>
                    <input
                      type="text"
                      value={vehicleRemark}
                      onChange={(e) => setVehicleRemark(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                      style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                      placeholder={t('common.formFields.placeholders.vehicleRemarkExample')}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.emptyTruckWeightKg')}</label>
                    <input
                      type="number"
                      value={emptyTruckWeightOverride}
                      onChange={(e) => setEmptyTruckWeightOverride(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      min="0"
                      className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.finalTruckWeightKg')}</label>
                    <input
                      type="number"
                      value={finalTruckWeight}
                      onChange={(e) => setFinalTruckWeight(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      min="0"
                      className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                      placeholder="0"
                    />
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {labels.items}
                </h3>
                <div className="space-y-3">
                  {orderData.items?.map((item, index) => (
                    <div
                      key={item.id || index}
                      className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl border-2"
                      style={{
                        backgroundColor: UI_COLORS.BACKGROUNDS.SECTION,
                        borderColor: UI_COLORS.BORDERS.LIGHT,
                      }}
                    >
                      <div className="md:col-span-2">
                        <div className="text-sm font-bold text-gray-800">
                          {item.product_name} <span className="text-gray-500 font-medium">({item.size_name})</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {labels.requested}: {item.quantity} {item.unit}
                        </div>
                        {item.remark && (
                          <div
                            className="text-[11px] font-semibold mt-1.5 px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 border border-yellow-200"
                            style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap', maxWidth: '300px' }}
                          >
                            {item.remark}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 md:col-span-2 gap-4">
                        <div className="text-right md:text-left">
                          <div className="text-[10px] font-bold text-gray-400 uppercase">{labels.actualQty}</div>
                          {orderData.is_direct_flow ? (
                            <input
                              type="number"
                              value={itemsUpdate[item.id]?.actual_quantity ?? ''}
                              onChange={(e) => setItemsUpdate(prev => ({ ...prev, [item.id]: { ...prev[item.id], actual_quantity: e.target.value } }))}
                              onWheel={(e) => e.target.blur()}
                              className="w-full px-2 py-1.5 rounded-lg border-2 text-sm font-bold text-blue-600 focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                              placeholder={String(item.quantity || 0)}
                              min="0"
                            />
                          ) : (
                            <div className="text-sm font-bold text-blue-600">{Math.round(parseFloat(item.actual_quantity || 0))} {item.unit}</div>
                          )}
                        </div>
                        <div className="text-right md:text-left">
                          <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">{labels.price}</div>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{t('deliveryOrdersModule.common.symbols.currency')}</span>
                            <input
                              type="number"
                              value={priceOverrides[item.id] ?? item.price ?? ''}
                              onChange={(e) => setPriceOverrides(prev => ({ ...prev, [item.id]: e.target.value }))}
                              onWheel={(e) => e.target.blur()}
                              className="w-full pl-5 pr-2 py-1.5 rounded-lg border-2 text-sm font-bold text-gray-800 focus:outline-none focus:border-blue-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              style={{
                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                borderColor: priceOverrides[item.id] !== undefined ? '#3b82f6' : UI_COLORS.FORMS.INPUT.border,
                              }}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="text-right md:text-left">
                          <div className="text-[10px] font-bold text-gray-400 uppercase">{labels.amount}</div>
                          <div className="text-sm font-bold text-gray-900">
                            {t('deliveryOrdersModule.common.symbols.currency')}{' '}
                            {(() => {
                              const ep = parseFloat(priceOverrides[item.id] ?? item.price ?? 0);
                              const rawQty = parseFloat(orderData.is_direct_flow ? (itemsUpdate[item.id]?.actual_quantity ?? item.quantity ?? 0) : (item.actual_quantity || 0));
                              const unit = (item.unit || '').toLowerCase();
                              const isPieces = unit === 'pieces' || unit === 'pcs' || unit === 'piece';
                              const qty = isPieces ? Math.round(rawQty) : rawQty;
                              return (ep * qty).toFixed(2);
                            })()}
                          </div>
                        </div>
                        <div className="text-right md:text-left">
                          <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">{labels.weight}</div>
                          {orderData.is_direct_flow ? (
                            <div className="relative">
                              <input
                                type="number"
                                value={itemsUpdate[item.id]?.actual_weight ?? ''}
                                onChange={(e) => setItemsUpdate(prev => ({ ...prev, [item.id]: { ...prev[item.id], actual_weight: e.target.value } }))}
                                onWheel={(e) => e.target.blur()}
                                className="w-full px-2 py-1.5 rounded-lg border-2 text-sm font-bold text-blue-600 focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                placeholder="0"
                                min="0"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">kg</span>
                            </div>
                          ) : isAccountExecutive ? (
                            <div className="relative">
                              <input
                                type="number"
                                value={weightOverrides[item.id] ?? item.actual_weight ?? ''}
                                onChange={(e) => setWeightOverrides(prev => ({ ...prev, [item.id]: e.target.value }))}
                                onWheel={(e) => e.target.blur()}
                                className="w-full px-2 py-1.5 rounded-lg border-2 text-sm font-bold text-blue-600 focus:outline-none focus:border-blue-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                style={{
                                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                  borderColor: weightOverrides[item.id] !== undefined ? '#3b82f6' : UI_COLORS.FORMS.INPUT.border,
                                }}
                                placeholder="0"
                                min="0"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">kg</span>
                            </div>
                          ) : (
                            <div className="text-sm font-bold text-blue-600">{Math.round(parseFloat(item.actual_weight || 0))} {t('deliveryOrdersModule.common.units.kg')}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Financial Adjustment Form */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 rounded-2xl border-2 bg-gray-50 border-gray-100">
                {/* Expenses */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {labels.expenses}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-400">{t('deliveryOrdersModule.common.symbols.currency')}</span>
                    <input
                      type="number"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full pl-8 pr-4 py-3 rounded-xl border-2 focus:outline-none focus:border-blue-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{
                        backgroundColor: UI_COLORS.FORMS.INPUT.background,
                        borderColor: UI_COLORS.FORMS.INPUT.border,
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <input
                    type="text"
                    value={expenseRemark}
                    onChange={(e) => setExpenseRemark(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:border-blue-500"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border,
                    }}
                    placeholder={placeholders.expenseRemark}
                  />
                </div>

                {/* Discount */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {labels.discount}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-400">{t('deliveryOrdersModule.common.symbols.currency')}</span>
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full pl-8 pr-4 py-3 rounded-xl border-2 focus:outline-none focus:border-green-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{
                        backgroundColor: UI_COLORS.FORMS.INPUT.background,
                        borderColor: UI_COLORS.FORMS.INPUT.border,
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <input
                    type="text"
                    value={discountRemark}
                    onChange={(e) => setDiscountRemark(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:border-green-500"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: UI_COLORS.FORMS.INPUT.border,
                    }}
                    placeholder={placeholders.discountRemark}
                  />
                </div>

                {/* Loading Cost Per KG */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {t('common.formFields.labels.loadingCostPerKg')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-400">{t('deliveryOrdersModule.common.symbols.currency')}</span>
                    <input
                      type="number"
                      value={loadingCostPerKg}
                      onChange={(e) => setLoadingCostPerKg(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full pl-8 pr-4 py-3 rounded-xl border-2 focus:outline-none focus:border-purple-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{
                        backgroundColor: UI_COLORS.FORMS.INPUT.background,
                        borderColor: UI_COLORS.FORMS.INPUT.border,
                      }}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Summary Section */}
              <div className="flex justify-end pt-4">
                <div className="w-full md:w-1/2 bg-blue-50 p-6 rounded-2xl border border-blue-100 space-y-3">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{labels.subtotal}</span>
                    <span className="font-medium">{t('deliveryOrdersModule.common.symbols.currency')} {effectiveSubtotal.toFixed(2)}</span>
                  </div>
                  {(() => {
                    const isChargeable = freightType === 'freight_chargeable';
                    const isToPay = freightType === 'free_delivery_freight_to_pay';
                    const pricingType = orderData?.freight_pricing_type || '';
                    const typeLabel = isChargeable
                      ? `Freight Chargeable${pricingType === 'per_kg' ? ' (Per KG)' : pricingType === 'fixed' ? ' (Fixed)' : ''}`
                      : isToPay ? 'Freight To Pay'
                      : freightType === 'ex_warehouse' ? 'Ex Warehouse'
                      : freightType === 'free_delivery_by_supplier' ? 'Free by Supplier'
                      : freightType === 'free_delivery_paid_by_company' ? 'Freight Paid by Company'
                      : labels.freight;
                    const color = isToPay ? 'text-green-600' : isChargeable ? 'text-gray-600' : 'text-gray-400';
                    const valueStr = isToPay
                      ? `- ${t('deliveryOrdersModule.common.symbols.currency')} ${freightAmount.toFixed(2)}`
                      : isChargeable
                      ? `${t('deliveryOrdersModule.common.symbols.currency')} ${freightAmount.toFixed(2)}`
                      : `${t('deliveryOrdersModule.common.symbols.currency')} ${freightAmount.toFixed(2)}`;
                    return (
                      <div className={`flex justify-between text-sm ${color}`}>
                        <span>{typeLabel}</span>
                        <span className="font-medium">{valueStr}</span>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{labels.loading}</span>
                    <span className="font-medium">
                      {t('deliveryOrdersModule.common.symbols.currency')}{' '}
                      {(() => {
                        const perKg = parseFloat(loadingCostPerKg) || 0;
                        const truckWt = parseFloat(orderData.calculated_truck_weight) || 0;
                        return truckWt > 0
                          ? (perKg * truckWt).toFixed(2)
                          : parseFloat(orderData.total_loading_cost || 0).toFixed(2);
                      })()}
                    </span>
                  </div>
                  {(parseFloat(expenses) > 0 || parseFloat(orderData.expenses) > 0) && (
                    <div className="flex justify-between text-sm text-orange-600">
                      <span>{labels.expenses}</span>
                      <span className="font-medium">{t('deliveryOrdersModule.common.symbols.currency')} {(parseFloat(expenses) || parseFloat(orderData.expenses) || 0).toFixed(2)}</span>
                    </div>
                  )}
                  {(parseFloat(discount) > 0 || parseFloat(orderData.discount) > 0) && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>{labels.discount}</span>
                      <span className="font-medium">- {t('deliveryOrdersModule.common.symbols.currency')} {(parseFloat(discount) || parseFloat(orderData.discount) || 0).toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="pt-2 border-t border-blue-200 flex justify-between text-sm font-bold text-blue-800">
                    <span>Total Without GST</span>
                    <span>{t('deliveryOrdersModule.common.symbols.currency')} {totalWithoutGst.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-sm text-gray-600">
                    <span>GST @ {gstPercentage}%</span>
                    <span className="font-medium">{t('deliveryOrdersModule.common.symbols.currency')} {totalGst.toFixed(2)}</span>
                  </div>

                  <div className="pt-3 border-t border-blue-300 flex justify-between text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    <span>{labels.totalAmount}</span>
                    <span>{t('deliveryOrdersModule.common.symbols.currency')} {totalWithGst.toFixed(2)}</span>
                  </div>

                  {/* Rounding Off - editable, auto-filled via ceiling */}
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span className="font-medium">Rounding Off</span>
                    <div className="relative w-32">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{t('deliveryOrdersModule.common.symbols.currency')}</span>
                      <input
                        type="number"
                        value={roundingOff !== '' ? roundingOff : autoRoundingOff}
                        onChange={(e) => setRoundingOff(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-full pl-5 pr-2 py-1.5 rounded-lg border-2 text-sm font-bold text-gray-800 focus:outline-none focus:border-blue-400 transition-colors text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        style={{
                          backgroundColor: UI_COLORS.FORMS.INPUT.background,
                          borderColor: roundingOff !== '' ? '#3b82f6' : UI_COLORS.FORMS.INPUT.border,
                        }}
                        step="1"
                      />
                    </div>
                  </div>

                  {/* Grand Total */}
                  <div className="pt-3 border-t-2 border-blue-400 flex justify-between text-2xl font-extrabold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    <span>Grand Total</span>
                    <span>{t('deliveryOrdersModule.common.symbols.currency')} {Math.round(grandTotal)}</span>
                  </div>
                </div>
              </div>

            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-4 p-6 border-t shrink-0"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl font-medium transition-colors"
            disabled={loading}
            style={{
              backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
              color: UI_COLORS.BUTTONS.SECONDARY.text,
            }}
          >
            {buttons.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !orderData}
            className="px-6 py-3 rounded-xl font-bold shadow-lg transition-all hover:shadow-xl disabled:opacity-70 flex items-center gap-2"
            style={{
              backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
              color: UI_COLORS.BUTTONS.PRIMARY.text,
            }}
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFileInvoiceDollar} />}
            <span>{loading ? buttons.generating : buttons.generate}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateChallanModal;
